import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { BriefDocument } from '../types/chat';

/**
 * Transport function signature for sending a conversation to the Claude
 * backend (or any compatible stand-in). Kept narrow so consumers — including
 * tests — can swap the implementation without depending on any stub module.
 */
export type ClaudeSendFn = (
    briefId: string,
    message: string,
    expectedMessageCount: number
) => Promise<{
    content: string;
    component: string | null;
    document: BriefDocument | null;
    isBriefComplete: boolean;
}>;

const ClaudeServiceContext = createContext<ClaudeSendFn | null>(null);

export interface ClaudeServiceProviderProps {
    sendMessage: ClaudeSendFn;
    children: ReactNode;
}

/**
 * Provides the Claude transport to the chat hook tree. Every consumer must
 * be wrapped with this provider; omitting it will throw at runtime.
 */
export function ClaudeServiceProvider({ sendMessage, children }: ClaudeServiceProviderProps) {
    return (
        <ClaudeServiceContext.Provider value={sendMessage}>
            {children}
        </ClaudeServiceContext.Provider>
    );
}

/** Access the active Claude transport. Throws if used outside a ClaudeServiceProvider. */
export function useClaudeService(): ClaudeSendFn {
    const fn = useContext(ClaudeServiceContext);
    if (!fn) throw new Error('useClaudeService must be used within a ClaudeServiceProvider');
    return fn;
}
